import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiSettings, FiUsers, FiHome, FiCheckCircle, FiFileText, FiHeadphones } from 'react-icons/fi';

function ProofPointsPanel({ data }) {
  const { t } = useTranslation();

  // Default execution discipline items (Card 1)
  const defaultExecutionDiscipline = [
    'End-to-end program execution: specification translation → build → verification → handover closure',
    'Configuration-controlled builds with traceable as-built documentation (program dependent)',
    'Nonconformance control with closure ownership (containment → root cause → corrective action)',
    'Sustainment mindset: spares planning, change impact assessment, and documentation continuity'
  ];
  
  // Default customer landscape items (Card 2)
  const defaultCustomerLandscape = [
    'DRDO laboratories and R&D establishments (prototype and qualification-oriented builds)',
    'Defence Public Sector Units and integrators (production deliveries and multi-stakeholder acceptance)',
    'Ministry of Defence / Government programs (compliance-led documentation and controlled communication)',
    'Foreign government / armed forces programs (export-oriented execution where permitted)'
  ];

  // Why Partner with HADEN tiles - get from translations
  const whyPartnerTitle = t('whyPartner.title', { defaultValue: 'Why Partner with HADEN' });
  const whyPartnerSubtitle = t('whyPartner.subtitle', { defaultValue: 'What sets us apart in defence system delivery' });
  const translatedWhyPartnerTiles = t('whyPartner.tiles', { returnObjects: true });
  
  const defaultWhyHadenTiles = [
    {
      icon: FiHome,
      title: 'Single-Source Accountability',
      description: 'Design to delivery under one roof — no multi-vendor coordination delays or finger-pointing.'
    },
    {
      icon: FiCheckCircle,
      title: 'First-Time FAT Clearance Focus',
      description: 'Evidence packs and test planning structured for acceptance closure, not rework cycles.'
    },
    {
      icon: FiFileText,
      title: 'Defence-Ready Documentation',
      description: 'Requirement traceability, as-built records, and witness testing support built into every program.'
    },
    {
      icon: FiHeadphones,
      title: 'Responsive Engineering Support',
      description: 'Direct access to the engineering team for queries, changes, and field support — not a service desk.'
    }
  ];
  
  const icons = [FiHome, FiCheckCircle, FiFileText, FiHeadphones];
  const whyHadenTiles = Array.isArray(translatedWhyPartnerTiles) 
    ? translatedWhyPartnerTiles.map((tile, idx) => ({ ...tile, icon: icons[idx] }))
    : defaultWhyHadenTiles;
  
  // Confidentiality footnote
  const confidentialityFootnote = t('confidentialityFootnote', { defaultValue: 'Programs executed under confidentiality norms; references shared on request subject to permissions.' });

  // Get translated content
  const title = t('proofPoints.title', { defaultValue: 'Track Record' });
  const subtitle = t('proofPoints.subtitle', { defaultValue: 'Reliability backed by execution' });
  const yearsLabel = t('proofPoints.stats.years', { defaultValue: 'Years Experience' });
  const systemsLabel = t('proofPoints.stats.systems', { defaultValue: 'Systems Delivered' });
  const docLabel = t('proofPoints.stats.documentation', { defaultValue: 'FAT-Ready Documentation' });
  const executionTitle = t('proofPoints.executionDiscipline.title', { defaultValue: 'Execution Discipline (AS9100D-Aligned)' });
  const customerTitle = t('proofPoints.customerLandscape.title', { defaultValue: 'Customer Landscape & Program Environments' });
  const ndaDisclosure = t('proofPoints.ndaDisclosure', { defaultValue: 'Program details are often NDA-bound; customer references and case summaries can be shared during technical discussions, subject to approvals.' });

  // Get translated arrays
  const translatedExecutionDiscipline = t('proofPoints.executionDiscipline.items', { returnObjects: true });
  const translatedCustomerLandscape = t('proofPoints.customerLandscape.items', { returnObjects: true });
  const executionDiscipline = Array.isArray(translatedExecutionDiscipline) ? translatedExecutionDiscipline : (data?.executionDiscipline || defaultExecutionDiscipline);
  const customerLandscape = Array.isArray(translatedCustomerLandscape) ? translatedCustomerLandscape : (data?.customerLandscape || defaultCustomerLandscape);

  return (
    <div 
      id="proof-points"
      className="panel panel-light scroll-mt-20" 
      data-testid="proof-points-panel"
    >
      <div className="panel-content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black mb-4 heading-premium">
            {title}
          </h2>
          <p className="text-xl max-w-3xl mx-auto subheading-premium">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-12 mb-16">
          {/* Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="text-center"
          >
            <div className="text-5xl font-black mb-2" style={{ color: '#101957' }}>
              {data?.metrics?.years_experience || '10+'}
            </div>
            <div className="text-lg font-bold" style={{ color: '#475569' }}>{yearsLabel}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.08 }}
            className="text-center"
          >
            <div className="text-5xl font-black mb-2" style={{ color: '#101957' }}>
              {data?.metrics?.systems_delivered || '50+'}
            </div>
            <div className="text-lg font-bold" style={{ color: '#475569' }}>{systemsLabel}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.16 }}
            className="text-center"
          >
            <div className="text-5xl font-black mb-2" style={{ color: '#101957' }}>
              {data?.metrics?.fat_success_rate || '✓'}
            </div>
            <div className="text-lg font-bold" style={{ color: '#475569' }}>{docLabel}</div>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Card 1: Execution Discipline */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="card-premium p-8"
          >
            <div className="flex items-center mb-6">
              <FiSettings className="text-3xl mr-3" style={{ color: '#101957' }} />
              <h3 className="text-xl font-black text-left" style={{ color: '#101957' }}>{executionTitle}</h3>
            </div>
            <ul className="space-y-4">
              {executionDiscipline.map((item, index) => (
                <li key={index} className="flex items-start font-medium text-left" style={{ color: '#475569' }}>
                  <span className="mr-3 mt-1 flex-shrink-0" style={{ color: '#101957' }}>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Card 2: Customer Landscape */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="card-premium p-8"
          >
            <div className="flex items-center mb-6">
              <FiUsers className="text-3xl mr-3" style={{ color: '#101957' }} />
              <h3 className="text-xl font-black text-left" style={{ color: '#101957' }}>{customerTitle}</h3>
            </div>
            <ul className="space-y-4">
              {customerLandscape.map((item, index) => (
                <li key={index} className="flex items-start font-medium text-left" style={{ color: '#475569' }}>
                  <span className="mr-3 mt-1 flex-shrink-0" style={{ color: '#101957' }}>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {/* Confidentiality footnote */}
            <p 
              className="mt-4 pt-4 text-sm italic text-left"
              style={{ color: '#94a3b8', borderTop: '1px solid rgba(16, 25, 87, 0.08)' }}
            >
              {confidentialityFootnote}
            </p>
          </motion.div>
        </div>

        {/* Why Partner with HADEN Section - NEW */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-16 pt-12"
          style={{ borderTop: '1px solid rgba(16, 25, 87, 0.1)' }}
        >
          <div className="text-center mb-10">
            <h3 
              className="text-2xl md:text-3xl font-bold mb-2"
              style={{ color: '#101957' }}
            >
              {whyPartnerTitle}
            </h3>
            <p 
              className="text-base"
              style={{ color: '#475569' }}
            >
              {whyPartnerSubtitle}
            </p>
          </div>

          {/* 4 Tiles in horizontal row */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyHadenTiles.map((tile, index) => {
              const Icon = tile.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="card-premium p-6"
                >
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: 'rgba(16, 25, 87, 0.08)' }}
                  >
                    <Icon className="text-2xl" style={{ color: '#101957' }} />
                  </div>
                  <h4 
                    className="text-base font-bold mb-2 text-left"
                    style={{ color: '#101957' }}
                  >
                    {tile.title}
                  </h4>
                  <p 
                    className="text-sm leading-relaxed text-left"
                    style={{ color: '#475569' }}
                  >
                    {tile.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* NDA Disclosure Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 text-center"
        >
          <p className="text-sm italic text-left" style={{ color: '#94a3b8' }}>
            {ndaDisclosure}
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default ProofPointsPanel;